import { api, API } from "@/lib/api";

let ctx = null;
let micStream = null;
let currentSource = null;
let speakToken = 0;
let configPromise = null;

export function getVoiceConfig() {
  if (!configPromise) {
    configPromise = api.get("/voice/config").then(({ data }) => data).catch(() => ({ enabled: false }));
  }
  return configPromise;
}

const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
synth?.getVoices();
synth?.addEventListener?.("voiceschanged", () => synth.getVoices());

const MALE_HINTS = /(lee|gordon|james|daniel|male|russell|william|oliver|arthur|aaron|fred|ralph|rishi|tom)/i;

function pickDeviceVoice() {
  const voices = synth?.getVoices() || [];
  const byLang = (code) => voices.filter((v) => (v.lang || "").replace("_", "-").toLowerCase().startsWith(code));
  const au = byLang("en-au");
  const gb = byLang("en-gb");
  return au.find((v) => MALE_HINTS.test(v.name)) || au[0] || gb.find((v) => MALE_HINTS.test(v.name)) || gb[0] || byLang("en")[0] || null;
}

export const plainText = (md = "") =>
  md
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[*_`#>|]+/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();

// Backup: the phone/computer's own voice (Australian English when available).
function speakWithDevice(text, token) {
  if (!synth || !text) return Promise.reject(new Error("no device voice"));
  return new Promise((resolve) => {
    if (token !== speakToken) { resolve(); return; }
    const u = new SpeechSynthesisUtterance(text);
    const v = pickDeviceVoice();
    if (v) { u.voice = v; u.lang = v.lang; } else { u.lang = "en-AU"; }
    u.rate = 1;
    u.pitch = 0.95;
    const guard = setTimeout(resolve, Math.max(5000, text.length * 110));
    const done = () => { clearTimeout(guard); resolve(); };
    u.onend = done;
    u.onerror = done;
    synth.cancel();
    synth.speak(u);
  });
}

// Must be called from a user tap so iOS/Safari allows audio playback later.
export function unlockAudio() {
  if (synth) {
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    synth.speak(u);
  }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx || ctx.state === "closed") ctx = new AC();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const buf = ctx.createBuffer(1, 1, 22050);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(ctx.destination);
  src.start(0);
  return ctx;
}

export async function openMic() {
  if (micStream && micStream.getAudioTracks().some((t) => t.readyState === "live")) return micStream;
  micStream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  return micStream;
}

export function closeMic() {
  micStream?.getTracks().forEach((t) => t.stop());
  micStream = null;
}

const pickMime = () =>
  ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find(
    (m) => window.MediaRecorder?.isTypeSupported?.(m)
  ) || "";

const VAD = { threshold: 0.018, startMs: 180, endSilenceMs: 1000, maxMs: 30000, restartIdleMs: 6000 };

function recordSegment(stream, { onLevel, shouldStop, idleRestart }) {
  return new Promise((resolve) => {
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    const srcNode = ctx.createMediaStreamSource(stream);
    srcNode.connect(analyser);
    const data = new Float32Array(analyser.fftSize);
    const t0 = performance.now();
    let loudSince = null;
    let spoke = false;
    let lastLoud = 0;
    let outcome = null;

    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      srcNode.disconnect();
      const blob = chunks.length ? new Blob(chunks, { type: rec.mimeType || mime || "audio/webm" }) : null;
      resolve({ outcome, blob, spoke });
    };

    const finish = (why) => {
      if (outcome) return;
      outcome = why;
      clearInterval(timer);
      onLevel?.(0);
      if (rec.state !== "inactive") rec.stop();
    };

    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      const now = performance.now();
      onLevel?.(Math.min(1, rms * 12));
      if (rms > VAD.threshold) {
        lastLoud = now;
        if (loudSince === null) loudSince = now;
        if (!spoke && now - loudSince >= VAD.startMs) spoke = true;
      } else {
        loudSince = null;
      }
      const stop = shouldStop();
      if (stop === "cancel") finish("cancel");
      else if (stop === "send") finish(spoke ? "speech" : "cancel");
      else if (spoke && now - lastLoud > VAD.endSilenceMs) finish("speech");
      else if (now - t0 > VAD.maxMs) finish(spoke ? "speech" : "idle");
      else if (!spoke && now - t0 > idleRestart) finish("idle");
    }, 50);

    rec.start(250);
  });
}

// Listens until the customer finishes a sentence. Returns a Blob, or null if they stayed quiet / it was cancelled.
export async function listenForUtterance({ onLevel, shouldStop, idleTimeoutMs = 25000 }) {
  const stream = await openMic();
  if (!ctx) unlockAudio();
  const started = performance.now();
  while (performance.now() - started < idleTimeoutMs) {
    const remaining = idleTimeoutMs - (performance.now() - started);
    const { outcome, blob } = await recordSegment(stream, { onLevel, shouldStop, idleRestart: Math.min(VAD.restartIdleMs, remaining) });
    if (outcome === "speech") return blob;
    if (outcome === "cancel") return null;
  }
  return null;
}

export async function transcribe(blob) {
  const ext = (blob.type.includes("mp4") && "mp4") || (blob.type.includes("ogg") && "ogg") || "webm";
  const form = new FormData();
  form.append("audio", blob, `speech.${ext}`);
  const { data } = await api.post("/voice/transcribe", form, { timeout: 45000 });
  return (data.text || "").trim();
}

export function stopSpeaking() {
  speakToken += 1;
  synth?.cancel();
  try { currentSource?.stop(); } catch { /* already stopped */ }
  currentSource = null;
}

// Plays Nick's reply for a stored message; resolves when it finishes or is interrupted.
// Falls back to the device's built-in voice if ElevenLabs can't speak.
export async function speakMessage(messageId, fallbackText = "") {
  if (!ctx) unlockAudio();
  const token = speakToken;
  let audio = null;
  let text = plainText(fallbackText);
  try {
    const res = await fetch(`${API}/voice/speak/${messageId}`);
    if (res.ok) {
      audio = await ctx.decodeAudioData(await res.arrayBuffer());
    } else {
      const body = await res.json().catch(() => ({}));
      if (body.text) text = body.text;
    }
  } catch {
    audio = null;
  }
  if (token !== speakToken) return undefined; // stopped while loading
  if (!audio) return speakWithDevice(text, token);
  return playBuffer(audio, token);
}

async function playBuffer(audio, token) {
  if (ctx.state === "suspended") await ctx.resume();
  if (token !== speakToken) return undefined;
  return new Promise((resolve) => {
    const src = ctx.createBufferSource();
    src.buffer = audio;
    src.connect(ctx.destination);
    src.onended = () => { if (currentSource === src) currentSource = null; resolve(); };
    currentSource = src;
    src.start(0);
  });
}

const b64ToBuffer = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;

// Plays sentence-by-sentence audio as it streams in, in order. Device voice for any sentence without audio.
export function createSpeechQueue({ onStart } = {}) {
  if (!ctx) unlockAudio();
  const token = speakToken;
  let chain = Promise.resolve();
  let count = 0;
  return {
    get count() { return count; },
    push(ev) {
      count += 1;
      const decoded = ev.audio ? ctx.decodeAudioData(b64ToBuffer(ev.audio)).catch(() => null) : Promise.resolve(null);
      chain = chain.then(async () => {
        const audio = await decoded;
        if (token !== speakToken) return;
        if (count && onStart) { onStart(); onStart = null; }
        if (audio) await playBuffer(audio, token);
        else await speakWithDevice(plainText(ev.text), token).catch(() => {});
      });
    },
    drain: () => chain,
  };
}

// Soft two-note "got it" chime so the customer knows Nick heard them.
export function chime() {
  if (!ctx) return;
  const t = ctx.currentTime;
  [[880, 0], [1318.5, 0.09]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t + delay);
    gain.gain.exponentialRampToValueAtTime(0.12, t + delay + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.22);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t + delay);
    osc.stop(t + delay + 0.25);
  });
}
